import flatten from 'lodash/flatten';
import property from 'lodash/property';

import * as d3 from 'd3';

import React from 'react';
import PropTypes from 'prop-types';

import { round3 as round } from 'utilities/math';

const canvasMargin = {
  top: 20,
  left: 100,
  right: 20,
  bottom: 20
};

const boxesMargin = {
  top: 0,
  right: 0,

  left: 20,
  bottom: 20
};

class BoxPlotComponent extends React.PureComponent {
  constructor (props) {
    super(props);

    this.createBoxPlot = () => {
      const boxesWidthMargin = boxesMargin.left + boxesMargin.right;
      const canvasWidthMargin = canvasMargin.left + canvasMargin.right;
      const width = this.props.canvasWidth - canvasWidthMargin - boxesWidthMargin;

      const boxesHeightMargin = boxesMargin.top + boxesMargin.bottom;
      const canvasHeightMargin = canvasMargin.top + canvasMargin.bottom;
      const height = this.props.canvasHeight - canvasHeightMargin - boxesHeightMargin;

      const { boxes } = this.props;

      const yDomain = d3.extent(
        flatten(
          boxes.map(box => [
            box.min, box.max
          ])
        )
      );

      const x = d3
        .scaleBand()
        .padding(0.6)
        .rangeRound([0, width])
        .domain(boxes.map(property('name')));

      const y = d3
        .scaleLinear()
        .domain(yDomain)
        .range([height, 0]);

      const svg = d3
        .select(this.svg)
        .select('#container.box-plot')
        .attr('transform', `translate(${canvasMargin.left}, ${canvasMargin.top})`);

      svg
        .select('#y-axis')
        .attr('class', 'y axis')
        .style('font-size', '14px')
        .call(d3.axisLeft(y));

      if (this.props.yAxisLabel) {
        svg
          .select('#y-axis-label')
          .text(this.props.yAxisLabel);
      }

      svg
        .select('#x-axis-container')
        .attr('transform', `
          translate(
            ${boxesMargin.left},
            ${height + boxesMargin.bottom}
          )
        `);

      svg
        .select('#x-axis')
        .style('font-size', '14px')
        .call(d3.axisBottom(x));

      if (this.props.xAxisLabel) {
        svg
          .select('#x-axis-label')
          .text(this.props.xAxisLabel)
          .attr('dx', width);
      }

      svg.selectAll('.box').remove();

      const box = svg
        .selectAll('.box')
        .data(boxes)
        .enter()
        .append('g')
        .attr('class', 'box')
        .attr('transform', d =>
          `translate(${x(d.name) + boxesMargin.left}, 0)`
        );

      const verticalLine = box
        .append('g')
        .attr('class', 'vertical-line');

      verticalLine
        .append('line')
        .attr('x1', x.bandwidth() / 2)
        .attr('x2', x.bandwidth() / 2)
        .attr('y1', d => y(d.lowerFence))
        .attr('y2', d => y(d.upperFence));

      verticalLine
        .append('line')
        .attr('x1', 0)
        .attr('x2', x.bandwidth())
        .attr('y1', d => y(d.lowerFence))
        .attr('y2', d => y(d.lowerFence));

      verticalLine
        .append('line')
        .attr('x1', 0)
        .attr('x2', x.bandwidth())
        .attr('y1', d => y(d.upperFence))
        .attr('y2', d => y(d.upperFence));

      verticalLine
        .append('text')
        .attr('dy', '0.4em')
        .attr('x', x.bandwidth() + 5)
        .attr('y', d => y(d.lowerFence))
        .text(d => round(d.lowerFence));

      verticalLine
        .append('text')
        .attr('dy', '0.4em')
        .attr('x', x.bandwidth() + 5)
        .attr('y', d => y(d.upperFence))
        .text(d => round(d.upperFence));

      const asteriskInnerContainer = verticalLine
        .selectAll('asterisk.inner')
        .data(d => d.innerOutliers)
        .enter()
        .append('svg')
        .attr('class', 'inner')
        .attr('x', (x.bandwidth() / 2) - 4)
        .attr('y', d => y(d) - 4.5)
        .attr('stroke', 'deepskyblue');

      const asteriskOuterContainer = verticalLine
        .selectAll('asterisk.outer')
        .data(d => d.outerOutliers)
        .enter()
        .append('svg')
        .attr('class', 'outer')
        .attr('x', (x.bandwidth() / 2) - 4)
        .attr('y', d => y(d) - 4.5)
        .attr('stroke', 'crimson');

      asteriskInnerContainer
        .append('svg:path')
        .attr('d', 'M4 0.5 L 4 7.5');

      asteriskInnerContainer
        .append('svg:path')
        .attr('d', 'M1 2 L 7 6');

      asteriskInnerContainer
        .append('svg:path')
        .attr('d', 'M7 2 L 1 6');

      asteriskOuterContainer
        .append('svg:path')
        .attr('d', 'M4 0.5 L 4 7.5');

      asteriskOuterContainer
        .append('svg:path')
        .attr('d', 'M1 2 L 7 6');

      asteriskOuterContainer
        .append('svg:path')
        .attr('d', 'M7 2 L 1 6');

      const body = box.append('g').attr('class', 'body');

      body
        .append('rect')
        .attr('x', 0)
        .attr('y', d => y(d.thirdQuantile))
        .attr('width', x.bandwidth())
        .attr('height', d => y(d.firstQuantile) - y(d.thirdQuantile));

      body
        .append('line')
        .attr('x1', 0)
        .attr('x2', x.bandwidth())
        .attr('y1', d => y(d.median))
        .attr('y2', d => y(d.median));

      body
        .append('text')
        .attr('dy', '.35em')
        .attr('x', x.bandwidth() + 5)
        .attr('y', d => y(d.median))
        .text(d => d.median.toFixed(3));

      body
        .append('text')
        .attr('dx', '-.4em')
        .attr('x', 0)
        .attr('y', d => y(d.thirdQuantile))
        .attr('text-anchor', 'end')
        .text(d => d.thirdQuantile.toFixed(3));

      body
        .append('text')
        .attr('dy', '.7em')
        .attr('dx', '-.4em')
        .attr('x', 0)
        .attr('y', d => y(d.firstQuantile))
        .attr('text-anchor', 'end')
        .text(d => d.firstQuantile.toFixed(3));
    };

    this.saveSvgNode = svg => {
      this.svg = svg;
    };
  }

  componentDidMount () {
    this.createBoxPlot();
  }

  componentDidUpdate () {
    this.createBoxPlot();
  }

  render () {
    return (
      <svg
        ref={this.saveSvgNode}
        width={this.props.canvasWidth}
        height={this.props.canvasHeight}
        data-test="boxPlot"
      >

        <g id="container" className="box-plot">
          <g id="x-axis-container">
            <g id="x-axis" />

            <text
              id="x-axis-label"

              dy="-5"
              fontSize="16"
              textAnchor="end"
            />
          </g>

          <g id="y-axis-container">
            <g id="y-axis" />

            <text
              id="y-axis-label"

              dy="15"
              fontSize="16"
              textAnchor="end"
              transform="rotate(-90)"
            />
          </g>
        </g>
      </svg>
    );
  }
}

BoxPlotComponent.propTypes = {
  boxes: PropTypes.arrayOf(
    PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,

      lowerFence: PropTypes.number.isRequired,
      upperFence: PropTypes.number.isRequired,

      firstQuantile: PropTypes.number.isRequired,
      thirdQuantile: PropTypes.number.isRequired,

      median: PropTypes.number.isRequired,

      innerOutliers: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
      outerOutliers: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired
    }).isRequired
  ).isRequired,

  canvasWidth: PropTypes.number.isRequired,
  canvasHeight: PropTypes.number.isRequired,

  xAxisLabel: PropTypes.string,
  yAxisLabel: PropTypes.string
};

export default BoxPlotComponent;
